import React, { useRef, useState } from 'react';
import { usePrintHook } from '../../hooks/usePrintHook';
import { q2Data, q2TrendData } from '../../data/q2Data';
import MarketingReport from '../MarketingReport';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, ComposedChart, Area
} from 'recharts';
import { formatCurrency, formatPercentage } from '../../utils/formatters';

// Colors for charts
const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFCC00', '#FF6666', '#5470C6', '#91CC75'
];

const StandaloneQ2Report: React.FC = () => {
  const reportRef = useRef<HTMLDivElement>(null);
  const { printRef } = usePrintHook();
  const [activeTab, setActiveTab] = useState<'overview' | 'trends' | 'sources' | 'full'>('overview');
  
  // Calculate quarterly totals
  const totalSales = q2Data.kpis.totalSales;
  const totalAdSpend = q2Data.kpis.adSpend;
  const totalGrossProfit = q2Data.kpis.grossProfit;
  const totalNetProfit = q2Data.kpis.netProfit;
  
  // Generate PDF from report
  const generatePDF = async () => {
    if (!reportRef.current) return;
    
    try {
      // Create filename for the PDF
      const fileName = `ROI_Marketing_Report_Q2_2025.pdf`;
      
      // Get the elements
      const reportElement = reportRef.current;
      
      // Temporarily hide print buttons (to prevent them from appearing in the PDF)
      const printButtons = reportElement.querySelectorAll('.print-button');
      printButtons.forEach(btn => {
        if (btn instanceof HTMLElement) {
          btn.style.display = 'none';
        }
      });
      
      // Capture the report content
      const canvas = await html2canvas(reportElement, {
        scale: 1.5, // Higher scale for better quality
        useCORS: true, // Allow images from other domains
        allowTaint: true, // Allow tainted canvas
        logging: false,
      });
      
      // Restore print buttons
      printButtons.forEach(btn => {
        if (btn instanceof HTMLElement) {
          btn.style.display = '';
        }
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Page setup (portrait A4)
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Load logo directly into PDF (separated from main content)
      const logoUrl = "https://roiconstruction.com/wp-content/uploads/2024/04/Marylands-Premier-Roofer-ROI-Construction-Roofing-Siding-Windows-Doors.png";
      
      // Create a temporary Image object to get logo dimensions
      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";
      logoImg.src = logoUrl;
      
      await new Promise<void>((resolve) => {
        logoImg.onload = () => resolve();
        if (logoImg.complete) resolve();
        setTimeout(resolve, 2000); // Failsafe timeout
      });
      
      // Add logo to first page
      const logoWidth = 90; // mm - logo width in PDF
      const logoX = (pdfWidth - logoWidth) / 2; // Center logo
      const logoY = 10; // Top margin
      
      try {
        const logoCanvas = document.createElement('canvas');
        logoCanvas.width = logoImg.width;
        logoCanvas.height = logoImg.height;
        const ctx = logoCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(logoImg, 0, 0, logoImg.width, logoImg.height);
          const logoDataUrl = logoCanvas.toDataURL('image/png');
          const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
          pdf.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
        }
      } catch (logoError) {
        console.error('Error adding logo:', logoError);
        // Continue without logo if there's an error
      }
      
      // Add title text
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(50, 50, 50);
      pdf.text('ROI Exteriors Marketing Performance Report', pdfWidth/2, 50, { align: 'center' });
      pdf.setFontSize(14);
      pdf.text('Q2 2025 (April - June)', pdfWidth/2, 60, { align: 'center' });
      
      // Calculate ratio to fit content to PDF
      const canvasRatio = canvas.height / canvas.width;
      const contentWidth = pdfWidth;
      const contentHeight = contentWidth * canvasRatio;
      
      // Add main content with offset to accommodate the logo and title
      const contentY = 70; // Start below the logo and title
      
      // Add content spread across multiple pages
      let heightLeft = contentHeight;
      let position = contentY - contentY; // Adjust initial position to show from the top
      let page = 1;
      
      pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
      heightLeft -= (pdfHeight - contentY);
      
      // Add more pages if content overflows
      while (heightLeft > 0) {
        pdf.addPage();
        page++;
        position = -(pdfHeight - contentY) * (page - 1) + contentY;
        pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
        heightLeft -= pdfHeight;
      }
      
      // Add footer on each page
      for (let i = 0; i < page; i++) {
        pdf.setPage(i + 1);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`ROI Exteriors Marketing Report - Page ${i + 1} of ${page}`, 10, pdfHeight - 5);
      }
      
      // Download PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };
  
  // Handle export to PDF
  const handleExportPDF = () => {
    generatePDF();
  };
  
  // Handle print
  const handlePrint = () => {
    if (reportRef.current) {
      // Cast the ref to the expected type
      printRef(reportRef as unknown as React.RefObject<HTMLElement>);
    }
  };

  // Custom tooltip formatter
  const customTooltipFormatter = (value: any, name: string) => {
    if (name === 'totalSales' || name === 'grossProfit' || name === 'adSpend' ||
        name === 'netProfit' || name === 'spend') {
      return [formatCurrency(value), name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())];
    }

    if (name === 'roi') {
      return [`${value.toFixed(2)}x`, 'ROI'];
    }

    if (name.includes('%') || name === 'grossMarginPct' || name === 'netMarginPct') {
      return [formatPercentage(value), name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())];
    }

    return [value, name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())];
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">
            Q2 2025 Marketing Report
          </h2>
          <div className="flex space-x-3">
            <button
              onClick={handlePrint}
              className="print-button px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
              </svg>
              Print
            </button>
            <button
              onClick={handleExportPDF}
              className="print-button px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              Export PDF
            </button>
            
          </div>

        </div>

        {/* Report content tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-1 py-4 text-sm font-medium ${
                  activeTab === 'overview'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Q2 Overview
              </button>
              <button
                onClick={() => setActiveTab('trends')}
                className={`px-1 py-4 text-sm font-medium ${
                  activeTab === 'trends'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Quarterly Trends
              </button>
              <button
                onClick={() => setActiveTab('sources')}
                className={`px-1 py-4 text-sm font-medium ${
                  activeTab === 'sources'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Source Analysis
              </button>
              <button
                onClick={() => setActiveTab('full')}
                className={`px-1 py-4 text-sm font-medium ${
                  activeTab === 'full'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Full Q2 Report
              </button>
            </nav>
          </div>
        </div>
        
        <div className="border p-4 rounded shadow-sm mb-4 bg-blue-50">
          <h3 className="text-lg font-semibold mb-2">Q2 2025 Consolidated Report</h3>
          <p className="mb-3">
            This report presents a comprehensive analysis of marketing performance across April, May, and June 2025.
          </p>
        </div>
        
        {/* Wrap report in a div with ref for PDF export */}
        <div ref={reportRef}>
          {/* Header with logo */}
          <div className="text-center mb-8">
            <img
              src="https://roiconstruction.com/wp-content/uploads/2024/04/Marylands-Premier-Roofer-ROI-Construction-Roofing-Siding-Windows-Doors.png"
              alt="ROI Construction Logo"
              className="mx-auto h-20 mb-4"
            />
            <h1 className="text-3xl font-bold text-gray-800">ROI Exteriors Marketing Performance Report</h1>
            <h2 className="text-xl text-gray-600">Q2 2025 (April - June)</h2>
          </div>

          {activeTab === 'overview' && (
            <div>
              {/* KPI Summary */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Q2 2025 Key Performance Metrics</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600 mb-1">Total Sales</div>
                    <div className="text-2xl font-bold text-blue-800">{formatCurrency(totalSales)}</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600 mb-1">Gross Profit</div>
                    <div className="text-2xl font-bold text-green-800">{formatCurrency(totalGrossProfit)}</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600 mb-1">Ad Spend</div>
                    <div className="text-2xl font-bold text-orange-800">{formatCurrency(totalAdSpend)}</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600 mb-1">Net Profit</div>
                    <div className="text-2xl font-bold text-purple-800">{formatCurrency(totalNetProfit)}</div>
                  </div>
                </div>

                <div className="text-center mb-8">
                  <div className="text-lg font-semibold">Net Profit Margin: <span className="text-blue-700">{formatPercentage(q2Data.kpis.netProfitPercent)}</span></div>
                  <div className="text-lg font-semibold">Gross Profit Margin: <span className="text-green-700">{formatPercentage(q2Data.kpis.grossProfitPercent)}</span></div>
                  <div className="text-lg font-semibold">ROI: <span className="text-orange-700">{(totalGrossProfit / totalAdSpend).toFixed(2)}x</span></div>
                  <div className="text-lg font-semibold">Total Jobs: <span className="text-purple-700">{q2Data.jobData.length}</span></div>
                </div>
              </div>

              {/* Executive Summary */}
              <div className="mb-8 p-6 bg-gray-50 rounded-lg">
                <h2 className="text-2xl font-bold mb-4 text-gray-800">Q2 2025 Executive Summary</h2>
                <div className="space-y-3">
                  <p>
                    During Q2 2025, ROI Exteriors achieved <strong>{formatCurrency(totalSales)}</strong> in total sales with 
                    a gross profit of <strong>{formatCurrency(totalGrossProfit)}</strong>. The total ad spend was 
                    <strong> {formatCurrency(totalAdSpend)}</strong>, resulting in a net profit of 
                    <strong> {formatCurrency(totalNetProfit)}</strong> and a net profit margin of 
                    <strong> {formatPercentage(q2Data.kpis.netProfitPercent)}</strong>.
                  </p>
                  
                  <p>
                    <strong>Monthly Performance Trend:</strong> The quarter showed varied performance, with June being the strongest month 
                    at <strong>{formatCurrency(q2TrendData.monthlyTrends[2].totalSales)}</strong> in sales compared to April&apos;s 
                    <strong> {formatCurrency(q2TrendData.monthlyTrends[0].totalSales)}</strong> and May&apos;s 
                    <strong> {formatCurrency(q2TrendData.monthlyTrends[1].totalSales)}</strong>.
                  </p>
                  
                  <p>
                    <strong>Top Performing Lead Sources:</strong> The highest ROI came from {q2TrendData.sourcePerformance[0].source} 
                    with an ROI of {q2TrendData.sourcePerformance[0].roi.toFixed(2)}x, followed by {q2TrendData.sourcePerformance[1].source} 
                    at {q2TrendData.sourcePerformance[1].roi.toFixed(2)}x.
                  </p>
                  
                  <p>
                    <strong>Trade Category Performance:</strong> The highest performing trade category was {q2Data.salesByTrade[0].trade} 
                    at <strong>{formatCurrency(q2Data.salesByTrade[0].totalSales)}</strong> in sales, 
                    followed by {q2Data.salesByTrade[1].trade} at <strong>{formatCurrency(q2Data.salesByTrade[1].totalSales)}</strong>.
                  </p>
                </div>
              </div>

              {/* Monthly Comparison Chart */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Monthly Performance Comparison</h2>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={q2TrendData.monthlyTrends}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" tickFormatter={(value) => formatCurrency(value)} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => formatCurrency(value)} />
                      <Tooltip formatter={customTooltipFormatter} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="totalSales" name="Total Sales" fill="#0088FE" />
                      <Bar yAxisId="left" dataKey="grossProfit" name="Gross Profit" fill="#00C49F" />
                      <Bar yAxisId="right" dataKey="adSpend" name="Ad Spend" fill="#FF8042" />
                      <Bar yAxisId="right" dataKey="netProfit" name="Net Profit" fill="#8884D8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'trends' && (
            <div>
              <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Q2 2025 Monthly Trends</h2>
              
              {/* Sales and Profit Trend */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-3">Sales and Profit Growth</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={q2TrendData.monthlyTrends}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => formatCurrency(value)} />
                      <Tooltip formatter={customTooltipFormatter} />
                      <Legend />
                      <Line type="monotone" dataKey="totalSales" name="Total Sales" stroke="#0088FE" strokeWidth={2} activeDot={{ r: 8 }} />
                      <Line type="monotone" dataKey="grossProfit" name="Gross Profit" stroke="#00C49F" strokeWidth={2} />
                      <Line type="monotone" dataKey="netProfit" name="Net Profit" stroke="#8884D8" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* ROI and Margin Trends */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-3">Profitability Metrics</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={q2TrendData.monthlyTrends}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" tickFormatter={(value) => value.toFixed(2) + 'x'} domain={[0, 'dataMax + 1']} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => formatPercentage(value)} domain={[0, 0.6]} />
                      <Tooltip formatter={customTooltipFormatter} />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="roi" name="ROI" stroke="#FF8042" strokeWidth={2} activeDot={{ r: 8 }} />
                      <Line yAxisId="right" type="monotone" dataKey="grossMarginPct" name="Gross Margin %" stroke="#00C49F" strokeWidth={2} />
                      <Line yAxisId="right" type="monotone" dataKey="netMarginPct" name="Net Margin %" stroke="#8884D8" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Sales Volume Trends */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-3">Sales Volume and Value</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={q2TrendData.monthlyTrends}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" domain={[0, 'dataMax + 2']} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => formatCurrency(value)} />
                      <Tooltip formatter={customTooltipFormatter} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="salesCount" name="Number of Sales" fill="#0088FE" />
                      <Line yAxisId="right" type="monotone" dataKey="avgSaleValue" name="Avg Sale Value" stroke="#FF8042" strokeWidth={2} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Summary Table */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-3">Monthly Performance Summary</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="py-2 px-4 text-left">Month</th>
                        <th className="py-2 px-4 text-right">Sales</th>
                        <th className="py-2 px-4 text-right">Sales Count</th>
                        <th className="py-2 px-4 text-right">Avg Sale Value</th>
                        <th className="py-2 px-4 text-right">Ad Spend</th>
                        <th className="py-2 px-4 text-right">Gross Profit</th>
                        <th className="py-2 px-4 text-right">Net Profit</th>
                        <th className="py-2 px-4 text-right">ROI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {q2TrendData.monthlyTrends.map((month, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="py-2 px-4">{month.month}</td>
                          <td className="py-2 px-4 text-right">{formatCurrency(month.totalSales)}</td>
                          <td className="py-2 px-4 text-right">{month.salesCount}</td>
                          <td className="py-2 px-4 text-right">{formatCurrency(month.avgSaleValue)}</td>
                          <td className="py-2 px-4 text-right">{formatCurrency(month.adSpend)}</td>
                          <td className="py-2 px-4 text-right">{formatCurrency(month.grossProfit)}</td>
                          <td className="py-2 px-4 text-right">{formatCurrency(month.netProfit)}</td>
                          <td className="py-2 px-4 text-right">{typeof month.roi === 'number' ? month.roi.toFixed(2) : '0.00'}x</td>
                        </tr>
                      ))}
                      <tr className="bg-blue-50 font-bold">
                        <td className="py-2 px-4">Q2 TOTAL</td>
                        <td className="py-2 px-4 text-right">{formatCurrency(totalSales)}</td>
                        <td className="py-2 px-4 text-right">{q2Data.jobData.length}</td>
                        <td className="py-2 px-4 text-right">{formatCurrency(totalSales / q2Data.jobData.length)}</td>
                        <td className="py-2 px-4 text-right">{formatCurrency(totalAdSpend)}</td>
                        <td className="py-2 px-4 text-right">{formatCurrency(totalGrossProfit)}</td>
                        <td className="py-2 px-4 text-right">{formatCurrency(totalNetProfit)}</td>
                        <td className="py-2 px-4 text-right">{(totalGrossProfit / totalAdSpend).toFixed(2)}x</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sources' && (
            <div>
              <h2 className="text-2xl font-bold mb-4 pb-2 border-b text-gray-800">Q2 2025 Lead Source Analysis</h2>
              
              {/* Top Performing Sources */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-3">Top Performing Lead Sources (Q2 Total)</h3>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={q2TrendData.sourcePerformance.slice(0, 8)} // Top 8 sources by ROI
                      margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="source" 
                        angle={-45} 
                        textAnchor="end"
                        height={70}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis yAxisId="left" tickFormatter={(value) => formatCurrency(value)} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, Math.max(8, ...q2TrendData.sourcePerformance.map(s => s.roi))]} />
                      <Tooltip formatter={customTooltipFormatter} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="totalSales" name="Total Sales" fill="#0088FE" />
                      <Bar yAxisId="left" dataKey="adSpend" name="Ad Spend" fill="#FF8042" />
                      <Line yAxisId="right" type="monotone" dataKey="roi" name="ROI" stroke="#8884D8" strokeWidth={3} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Source Performance by Month */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-3">Lead Source Performance by Month</h3>
                <div className="mb-4">
                  <p className="text-gray-600">This chart shows the sales performance of top lead sources across each month of Q2.</p>
                </div>
                
                {/* Source monthly performance chart */}
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={q2TrendData.leadSourceTrends
                        .filter(source => source.q2Total.sales > 15000) // Only show major sources
                        .map(source => ({
                          source: source.source,
                          April: source.april.sales,
                          May: source.may.sales,
                          June: source.june.sales
                        }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(value) => formatCurrency(value)} />
                      <YAxis 
                        dataKey="source" 
                        type="category" 
                        tick={{ fontSize: 12 }}
                        width={150}
                      />
                      <Tooltip formatter={customTooltipFormatter} />
                      <Legend verticalAlign="top" />
                      <Bar dataKey="April" fill="#0088FE" stackId="a" />
                      <Bar dataKey="May" fill="#00C49F" stackId="a" />
                      <Bar dataKey="June" fill="#FF8042" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* ROI Monthly Trends for Top Sources */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-3">ROI Trends for Top Sources</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" type="category" allowDuplicatedCategory={false} />
                      <YAxis tickFormatter={(value) => typeof value === 'number' ? value.toFixed(1) + 'x' : '0.0x'} />
                      <Tooltip formatter={(value) => [typeof value === 'number' ? value.toFixed(2) + 'x' : '0.00x', 'ROI']} />
                      <Legend />
                      
                      {q2TrendData.leadSourceTrends
                        .filter(source => source.q2Total.sales > 15000) // Only show major sources
                        .map((source, index) => (
                          <Line
                            key={source.source}
                            type="monotone"
                            data={[
                              {month: 'April', roi: source.april.roi, name: source.source},
                              {month: 'May', roi: source.may.roi, name: source.source},
                              {month: 'June', roi: source.june.roi, name: source.source}
                            ]}
                            dataKey="roi"
                            name={source.source}
                            stroke={COLORS[index % COLORS.length]}
                            strokeWidth={2}
                            activeDot={{ r: 6 }}
                          />
                        ))
                      }
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Lead Source Performance Summary Table */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-3">Lead Source Performance Summary</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="py-2 px-4 text-left">Lead Source</th>
                        <th className="py-2 px-4 text-right">Total Sales</th>
                        <th className="py-2 px-4 text-right">Job Count</th>
                        <th className="py-2 px-4 text-right">Ad Spend</th>
                        <th className="py-2 px-4 text-right">Gross Profit</th>
                        <th className="py-2 px-4 text-right">ROI</th>
                        <th className="py-2 px-4 text-right">Cost Per Sale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {q2TrendData.sourcePerformance.map((source, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="py-2 px-4">{source.source}</td>
                          <td className="py-2 px-4 text-right">{formatCurrency(source.totalSales)}</td>
                          <td className="py-2 px-4 text-right">{source.count}</td>
                          <td className="py-2 px-4 text-right">{formatCurrency(source.adSpend)}</td>
                          <td className="py-2 px-4 text-right">{formatCurrency(source.grossProfit)}</td>
                          <td className="py-2 px-4 text-right">{source.roi.toFixed(2)}x</td>
                          <td className="py-2 px-4 text-right">{formatCurrency(source.costPerSale)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'full' && (
            <div>
              <MarketingReport 
                data={q2Data} 
                hideHeader={true} // Hide the header to avoid duplication
              />
            </div>
          )}

          {/* Q2 Recommendations and Forward Looking Analysis */}
          <div className="mt-8 mb-8 p-6 bg-gray-50 rounded-lg">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Q2 Strategic Recommendations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">Lead Source Optimization</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <strong>Top Performers:</strong> Increase investment in {q2TrendData.sourcePerformance[0].source} and {q2TrendData.sourcePerformance[1].source}, 
                    which show the highest ROI at {q2TrendData.sourcePerformance[0].roi.toFixed(2)}x and {q2TrendData.sourcePerformance[1].roi.toFixed(2)}x respectively.
                  </li>
                  <li>
                    <strong>Reallocate Budget:</strong> Reduce spending on low-performing channels like
                    {q2TrendData.sourcePerformance
                      .filter(s => s.adSpend > 1000 && s.roi < 1.2)
                      .slice(0, 2)
                      .map(s => ` ${s.source} (${s.roi.toFixed(2)}x ROI)`)
                      .join(", ")}.
                  </li>
                  <li>
                    <strong>Channel Growth:</strong> Consider scaling successful channels based on Q2 performance trends.
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3">Product Strategy</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    <strong>High-Margin Focus:</strong> Emphasize {q2Data.salesByTrade[0].trade} and {q2Data.salesByTrade[1].trade}, 
                    which represent {formatPercentage((q2Data.salesByTrade[0].totalSales + q2Data.salesByTrade[1].totalSales) / totalSales)} of Q2 sales.
                  </li>
                  <li>
                    <strong>Seasonal Planning:</strong> With Q3 approaching, prepare for summer peak season with targeted campaigns.
                  </li>
                  <li>
                    <strong>Cross-Selling:</strong> Implement strategies to bundle complementary services based on Q2 customer patterns.
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-3">Q3 2025 Outlook</h3>
              <p className="mb-3">
                Based on Q2 performance, ROI Exteriors is positioned for continued growth in Q3. 
                The company should aim to maintain the momentum from June&apos;s strong performance while 
                improving upon the {formatPercentage(q2Data.kpis.grossProfitPercent)} gross profit margin achieved in Q2.
              </p>
              <p>
                Key focus areas should include expanding successful lead sources, optimizing ad spend allocation, 
                and capitalizing on high-demand services as we enter the peak construction season.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StandaloneQ2Report;